/*
 * Vieux Materials drawer (blocks/vieux-product-materials.liquid): text tabs
 * under a scroll-snapping track of material slides. A tab click scrolls the
 * track to its slide; a swipe (or any scroll) moves the active tab to the
 * nearest slide. No arrow buttons (operator-approved prototype v9,
 * 2026-10-03); the keyboard uses the tab pattern's arrow keys.
 */
if (!customElements.get('vieux-materials')) {
  class VieuxMaterials extends HTMLElement {
    connectedCallback() {
      this.abort?.abort();
      this.abort = new AbortController();
      const { signal } = this.abort;
      const track = this.querySelector('[data-mt-track]');
      if (!(track instanceof HTMLElement)) return;
      this.track = track;

      for (const tab of this.tabs()) {
        tab.addEventListener('click', () => this.go(Number(tab.dataset.mtTab)), { signal });
      }
      track.addEventListener('scroll', () => this.sync(), { passive: true, signal });
      this.querySelector('[role="tablist"]')?.addEventListener('keydown', (event) => this.onKey(event), { signal });
    }

    /**
     * Tab pattern keys: Left / Right move to the previous / next tab (wrapping),
     * Home / End to the first / last. Focus and the slide follow; only the
     * active tab is in the tab order (roving tabindex, kept by sync()).
     * @param {KeyboardEvent} event
     */
    onKey(event) {
      const tabs = this.tabs();
      const current = tabs.indexOf(/** @type {HTMLButtonElement} */ (document.activeElement));
      if (current < 0) return;
      let next = -1;
      if (event.key === 'ArrowRight') next = (current + 1) % tabs.length;
      else if (event.key === 'ArrowLeft') next = (current - 1 + tabs.length) % tabs.length;
      else if (event.key === 'Home') next = 0;
      else if (event.key === 'End') next = tabs.length - 1;
      if (next < 0) return;
      event.preventDefault();
      tabs[next].focus();
      this.go(next);
    }

    disconnectedCallback() {
      this.abort?.abort();
    }

    /** @returns {HTMLButtonElement[]} */
    tabs() {
      return Array.from(this.querySelectorAll('[data-mt-tab]'));
    }

    /** @returns {HTMLElement[]} */
    slides() {
      return Array.from(this.querySelectorAll('[data-mt-slide]'));
    }

    /** @param {number} index */
    go(index) {
      const slides = this.slides();
      const slide = slides[index];
      if (!slide || !this.track) return;
      const left = slide.offsetLeft - slides[0].offsetLeft;
      const smooth = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.track.scrollTo({ left, behavior: smooth ? 'smooth' : 'auto' });
    }

    sync() {
      const slides = this.slides();
      if (!slides.length || !this.track) return;
      const x = this.track.scrollLeft;
      let best = 0;
      let bestDist = Infinity;
      slides.forEach((slide, i) => {
        const d = Math.abs(slide.offsetLeft - slides[0].offsetLeft - x);
        if (d < bestDist) {
          bestDist = d;
          best = i;
        }
      });
      this.tabs().forEach((tab, i) => {
        const on = i === best;
        tab.classList.toggle('on', on);
        tab.setAttribute('aria-selected', on ? 'true' : 'false');
        tab.tabIndex = on ? 0 : -1;
      });
    }
  }
  customElements.define('vieux-materials', VieuxMaterials);
}
